import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, CircularProgress, Card, Button } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import FormsAPI from '../../api/FormsAPI';
import FormSubmissionsAPI from '../../api/FormSubmissionsAPI';
import AnalyticsAPI from '../../api/AnalyticsAPI';
import generateSubmissionPdf from './generateSubmissionPdf';
import InteractiveFieldRenderer from './InteractiveFieldRenderer';
import RichEditor from '../Admin/FormBuilder/components/builder/RichEditor';
import { getFieldTheme, getFieldWidth, buildBoxShadow, getSubmitButtonSx, getBackButtonSx, getProgressBarColor } from '../Admin/FormBuilder/utils/fieldStyling';
import { resolveActiveStyling } from '../Admin/FormBuilder/utils/deviceStyling';
import { normalizeTextListItems } from '../Admin/FormBuilder/utils/textListItems';
import { buildConsentAnswerValue } from '../Admin/FormBuilder/utils/consentItems';
import { serializeDateFieldValue } from '../Admin/FormBuilder/utils/dateFieldValue';
import { DEFAULT_SUBMISSION_MESSAGE, DEFAULT_SUBMISSION_STYLING, DEFAULT_FORM_SETTINGS } from '../Admin/FormBuilder/utils/submissionDefaults';
import { normalizeHref } from '../Admin/FormBuilder/utils/url';
import { splitFieldsIntoPages } from '../Admin/FormBuilder/utils/pages';
import { validateFields } from './utils/validateFields';
import { useResponsiveLayout } from '../../utils/responsive';
import { MESSAGE_READY, postSubmittedToParent } from './utils/parentMessaging';

const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

const getFormIdFromUrl = () => {
    // /event-registration/{id}/ is the same page, used for embeds on TMT.
    const match = window.location.pathname.match(/\/(?:form|event-registration)\/(\d+)\/?/);
    return match ? Number(match[1]) : null;
};

const sideValue = (value) => (value === 'auto' ? 'auto' : `${value}px`);

const FormFillPage = () => {
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [form, setForm] = useState(null);
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);
    // True once the submission receipt was handed to the TMT page embedding
    // this form — that page takes the visitor to their pass, so this form
    // shows a short "loading your pass" state instead of its own message.
    const [handedToParent, setHandedToParent] = useState(false);
    // Set the moment a submit succeeds, before any re-render. The TMT page
    // navigates away as soon as it receives the receipt, sooner than React
    // can re-render with `submitted`, so the leave warning reads this instead.
    const submittedRef = useRef(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    // Measured right before switching to the confirmation view, so the
    // message's container can fill the same height the form itself occupied
    // — without it, "auto" margins on the message card have no extra space
    // to center within, since a flex container with no defined height just
    // hugs its (shorter) content.
    const formCardRef = useRef(null);
    const [submittedMinHeight, setSubmittedMinHeight] = useState(null);
    // Bot protection — always rendered outside `form.fields`, so it isn't a
    // form-builder field an admin can delete or reorder away.
    const [captchaToken, setCaptchaToken] = useState('');
    const [isCaptchaVerifying, setIsCaptchaVerifying] = useState(true);
    // Turnstile's own auto-render only scans the DOM for `.cf-turnstile`
    // elements ONCE, when its script finishes loading — on a multi-page form
    // the captcha container doesn't exist yet if the visitor is still on an
    // earlier page at that moment, so it would never get rendered into.
    // Rendering it explicitly (once the script is ready AND the container
    // has actually mounted) avoids that race entirely.
    const [turnstileReady, setTurnstileReady] = useState(false);
    const turnstileRef = useRef(null);
    const turnstileWidgetIdRef = useRef(null);

    const { isMobile } = useResponsiveLayout();
    const formId = useMemo(getFormIdFromUrl, []);
    // A form with no `page-break` fields is just one page (pages.length ===
    // 1) — fully backward compatible, no Next/Back/progress UI renders below.
    const pages = useMemo(() => splitFieldsIntoPages(form?.fields ?? []), [form?.fields]);
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const isLastPage = currentPageIndex === pages.length - 1;
    // `vh` is relative to the IFRAME's own viewport when embedded, not the
    // outer page's — forcing a `100vh` backdrop there would feed back into
    // the resize-reporting effect below (taller iframe -> bigger 100vh ->
    // reported height grows again -> iframe grows again...), runaway-growing
    // the embed on every tick. Only the standalone (non-iframe) page has a
    // real, fixed viewport that's safe to size a full-height backdrop against.
    const isEmbedded = useMemo(() => window.parent !== window, []);
    const mountTimeRef = useRef(Date.now());
    const viewTrackedRef = useRef(false);
    const formSettings = form?.settings?.formSettings ?? DEFAULT_FORM_SETTINGS;
    const turnstileSiteKey = form?.turnstile_site_key || '';
    const isTurnstileConfigured = Boolean(turnstileSiteKey.trim());
    const resolvedFormStyling = useMemo(
        () => resolveActiveStyling(form?.settings?.formStyling, isMobile ? 'mobile' : 'desktop'),
        [form?.settings?.formStyling, isMobile]
    );
    const fieldTheme = useMemo(
        () => getFieldTheme(resolvedFormStyling, { systemFontOnly: !!formSettings.gdprFont }),
        [resolvedFormStyling?.primaryColor, isMobile, formSettings.gdprFont]
    );

    useEffect(() => {
        if (!formId) {
            setNotFound(true);
            setLoading(false);
            return;
        }

        FormsAPI.getPublicForm(formId)
            .then((res) => {
                if (res?.data) {
                    setForm(res.data);
                } else {
                    setNotFound(true);
                }
            })
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
    }, [formId]);

    // Lets an embedding page know the form has rendered. Carries nothing
    // sensitive, so any parent may receive it.
    useEffect(() => {
        if (!form || window.parent === window) return;
        window.parent.postMessage({ type: MESSAGE_READY, formId }, '*');
    }, [form, formId]);

    // Loads the Turnstile script once and tracks when it's actually usable
    // (window.turnstile defined) — rendering is done explicitly in the
    // effect below rather than relying on Turnstile's own auto-render scan,
    // which only runs once at script-load time and would miss a captcha
    // container that mounts later (e.g. after paging to a multi-page form's
    // last page).
    useEffect(() => {
        if (!isTurnstileConfigured) return;

        if (window.turnstile) {
            setTurnstileReady(true);
            return;
        }

        const existingScript = document.querySelector(`script[src="${TURNSTILE_SCRIPT_SRC}"]`);
        if (existingScript) {
            existingScript.addEventListener('load', () => setTurnstileReady(true));
            return;
        }

        const script = document.createElement('script');
        script.src = TURNSTILE_SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        script.addEventListener('load', () => setTurnstileReady(true));
        document.head.appendChild(script);
    }, [isTurnstileConfigured]);

    // Explicitly renders the widget into turnstileRef's container once the
    // script is ready AND that container actually exists in the DOM (i.e.
    // once the last page — the only page it's rendered on — is showing).
    // Re-runs (and cleans up the old widget) whenever the container
    // unmounts/remounts, e.g. paging away from and back to the last page.
    useEffect(() => {
        if (!isTurnstileConfigured || !turnstileReady || !isLastPage || !turnstileRef.current) return;
        if (turnstileWidgetIdRef.current) return;

        turnstileWidgetIdRef.current = window.turnstile.render(turnstileRef.current, {
            sitekey: turnstileSiteKey,
            theme: 'light',
            size: 'normal',
            action: 'form_submit',
            'before-interactive-callback': () => {
                setCaptchaToken('');
                setIsCaptchaVerifying(true);
            },
            callback: (token) => {
                setCaptchaToken(token || '');
                setIsCaptchaVerifying(false);
            },
            'expired-callback': () => {
                setCaptchaToken('');
                setIsCaptchaVerifying(true);
            },
            'error-callback': () => {
                setCaptchaToken('');
                setIsCaptchaVerifying(true);
            },
        });

        return () => {
            if (turnstileWidgetIdRef.current) {
                window.turnstile.remove(turnstileWidgetIdRef.current);
                turnstileWidgetIdRef.current = null;
            }
        };
    }, [isTurnstileConfigured, turnstileReady, isLastPage, turnstileSiteKey]);

    // Records one view per real page load. Guarded by a ref (not just the
    // effect's dependency array) because, unlike the resize effect below,
    // this write isn't idempotent — React 18 StrictMode's dev-only double
    // mount would otherwise double-count every view.
    useEffect(() => {
        if (!formId || viewTrackedRef.current) return;
        viewTrackedRef.current = true;
        AnalyticsAPI.trackView(formId).catch(() => {});
    }, [formId]);

    // Reports this page's actual content height to a parent window embedding it in
    // an iframe (see EmbedSnippetDialog.jsx's resize script), so the iframe can grow
    // to fit the form instead of showing an internal scrollbar.
    useEffect(() => {
        if (window.parent === window) return;

        let lastHeight = null;
        let rafId = null;

        // Coalesced into a single post per animation frame, and skipped
        // entirely when the height hasn't actually changed — a Select menu
        // opening/closing briefly toggles body overflow/padding (MUI's
        // scroll lock) and mounts/unmounts a Portal, both of which fire this
        // ResizeObserver without the form's real content height changing.
        // Posting those through unthrottled made the parent page's
        // `iframe.style.height` write jump the embedding page's scroll
        // position on every select interaction.
        const postHeight = () => {
            if (rafId !== null) return;
            rafId = requestAnimationFrame(() => {
                rafId = null;
                const height = document.documentElement.scrollHeight;
                if (height === lastHeight) return;
                lastHeight = height;
                window.parent.postMessage({ type: 'avocado-form-resize', formId, height }, '*');
            });
        };

        postHeight();

        const resizeObserver = new ResizeObserver(postHeight);
        resizeObserver.observe(document.body);
        window.addEventListener('load', postHeight);

        return () => {
            if (rafId !== null) cancelAnimationFrame(rafId);
            resizeObserver.disconnect();
            window.removeEventListener('load', postHeight);
        };
    }, [formId]);

    // "Save progress for forms" — warns the visitor before a real tab-close/
    // refresh/navigation-away, once they've actually entered something. Only
    // the native browser dialog can intercept a real unload (a custom modal
    // can't), matching the existing precedent in PDFplaceholderCanvas.jsx.
    useEffect(() => {
        if (!formSettings.saveProgress) return;

        const handler = (e) => {
            if (submittedRef.current) return;
            const hasEnteredData = Object.values(formData).some(
                (value) => value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && value.length === 0)
            );
            if (!hasEnteredData) return;
            e.preventDefault();
            e.returnValue = '';
        };

        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [formSettings.saveProgress, formData]);

    const handleFieldChange = (fieldId, value) => {
        setFormData((prev) => ({ ...prev, [fieldId]: value }));
        setErrors((prev) => ({ ...prev, [fieldId]: undefined }));
    };

    const handleNextPage = () => {
        const currentFields = pages[currentPageIndex]?.fields ?? [];
        const nextErrors = validateFields(currentFields, formData);

        if (Object.keys(nextErrors).length > 0) {
            setErrors((prev) => ({ ...prev, ...nextErrors }));
            return;
        }

        setCurrentPageIndex((index) => Math.min(index + 1, pages.length - 1));
    };

    const handleBackPage = () => {
        setCurrentPageIndex((index) => Math.max(index - 1, 0));
    };

    const handleSubmit = async () => {
        const fields = form?.fields ?? [];
        // Defense-in-depth: re-validates every page's fields, not just the
        // current one — Next already gated each page on the way here, but
        // this is the last line of defense before the real submit.
        const nextErrors = validateFields(fields, formData);

        if (Object.keys(nextErrors).length > 0) {
            setErrors(nextErrors);
            return;
        }

        if (!isTurnstileConfigured) {
            setSubmitError('This form is temporarily unavailable because captcha is not configured. Please contact support.');
            return;
        }

        if (!captchaToken) {
            setSubmitError('Please complete the captcha challenge.');
            return;
        }

        setSubmitError('');
        setSubmitting(true);

        const isUploadType = (type) => type === 'file' || type === 'signature';

        // __addressSelected is a client-side-only marker (tracks whether the
        // visitor picked a suggestion from the address autocomplete box) —
        // it must never be submitted as part of the answer value.
        const submittedValue = (field) => {
            const value = formData[field.id] ?? null;
            // Untouched text-list fields never got a formData entry — submit
            // their prefill defaults as-is rather than losing them. Also
            // normalizes old string-shaped items (pre-dating this field
            // becoming interactive) so the backend never receives that shape.
            if (field.type === 'text-list') return normalizeTextListItems(value ?? field.items);
            if (field.type === 'date') return serializeDateFieldValue(value);
            if (field.type !== 'address-group' || !value) return value;
            const { __addressSelected, ...rest } = value;
            return rest;
        };

        const answers = fields.map((field) => ({
            field_id: field.id,
            label: field.label,
            type: field.type,
            value: isUploadType(field.type) ? null : submittedValue(field),
        }));

        const payload = new FormData();
        payload.append('answers', JSON.stringify(answers));
        payload.append('captcha_token', captchaToken);
        const timeSpentSeconds = Math.round((Date.now() - mountTimeRef.current) / 1000);
        payload.append('time_spent_seconds', String(timeSpentSeconds));

        if (formSettings.enableTimezone) {
            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const offset = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'longOffset' })
                .formatToParts(new Date())
                .find((part) => part.type === 'timeZoneName')?.value;
            payload.append('timezone', offset ? `${timeZone} (${offset})` : timeZone);
        }

        const notifications = form?.settings?.notifications ?? {};
        const needsPdf = notifications.emailNotification?.sendAsPdf || notifications.autoResponder?.sendAsPdf;
        if (needsPdf) {
            // The submitted answer for a consent field is a raw array of
            // booleans (see submittedValue above) — that's the shape the
            // backend expects, but formatConsentAnswer (used by the PDF
            // renderer) expects the {content, checked} shape the backend
            // normalizes it into after the fact. Build that same pairing
            // here, only for the PDF, so the actual submitted payload is
            // untouched.
            const pdfAnswers = answers.map((answer) => {
                if (answer.type !== 'consent') return answer;
                const field = fields.find((f) => f.id === answer.field_id);
                return { ...answer, value: buildConsentAnswerValue(field, answer.value) };
            });
            const pdfBlob = generateSubmissionPdf(form?.name, pdfAnswers);
            payload.append('submission_pdf', pdfBlob, 'submission.pdf');
        }

        fields.forEach((field) => {
            if (!isUploadType(field.type)) return;
            const fieldValue = formData[field.id];
            if (!fieldValue) return;
            const filename = fieldValue.name ?? `signature-${field.id}.png`;
            payload.append(`field_${field.id}`, fieldValue, filename);
        });

        try {
            const result = await FormSubmissionsAPI.submitForm(formId, payload);
            submittedRef.current = true;

            // A form linked to a TMT event, embedded on TMT: TMT moves the
            // visitor on to their pass, so this form neither redirects nor
            // shows its own confirmation.
            if (postSubmittedToParent(form, result?.data)) {
                setHandedToParent(true);
                setSubmitted(true);
                return;
            }

            const isRedirect = form?.settings?.actionType === 'redirect' && form?.settings?.redirectUrl;
            if (isRedirect) {
                setSubmitted(true);
                // Delayed so the "Redirecting…" card actually gets a chance to
                // paint, rather than the browser navigating away instantly.
                window.setTimeout(() => {
                    window.location.href = normalizeHref(form.settings.redirectUrl);
                }, 1200);
            } else {
                setSubmittedMinHeight(formCardRef.current?.offsetHeight ?? null);
                setSubmitted(true);
            }
        } catch (error) {
            setSubmitError(error?.message || 'Failed to submit form. Please try again.');
            setCaptchaToken('');
            setIsCaptchaVerifying(true);
            if (window.turnstile && turnstileWidgetIdRef.current) {
                window.turnstile.reset(turnstileWidgetIdRef.current);
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress size={28} />
            </Box>
        );
    }

    if (notFound || !form) {
        return (
            <Box sx={{ py: 8, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                    This form is unavailable.
                </Typography>
            </Box>
        );
    }

    const settings = form.settings ?? {};
    const styling = resolveActiveStyling(settings.styling ?? DEFAULT_SUBMISSION_STYLING, isMobile ? 'mobile' : 'desktop');
    const formStyling = resolvedFormStyling;

    if (submitted) {
        if (handedToParent) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        py: 8,
                    }}
                >
                    <CircularProgress size={28} />
                    <Typography variant="body1" color="text.secondary">
                        Submitted — loading your pass…
                    </Typography>
                </Box>
            );
        }

        if (settings.actionType === 'redirect' && settings.redirectUrl) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        py: 8,
                    }}
                >
                    <CircularProgress size={28} />
                    <Typography variant="body1" color="text.secondary">
                        Redirecting…
                    </Typography>
                </Box>
            );
        }

        const cardSx = {
            backgroundColor: styling.background ?? '#FFFFFF',
            borderRadius: `${styling.cornerRadius}px`,
            margin: `${sideValue(styling.margin.top)} ${sideValue(styling.margin.right)} ${sideValue(styling.margin.bottom)} ${sideValue(styling.margin.left)}`,
            padding: `${styling.padding.top}px ${styling.padding.right}px ${styling.padding.bottom}px ${styling.padding.left}px`,
            '& .ProseMirror': { fontWeight: styling.fontWeight },
        };

        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: submittedMinHeight ?? undefined }}>
                <Card elevation={3} sx={{ ...cardSx, width: '100%', maxWidth: 600, textAlign: 'center' }}>
                    <RichEditor value={settings.message ?? DEFAULT_SUBMISSION_MESSAGE} readOnly />
                </Card>
            </Box>
        );
    }

    const formBorderStyle = formStyling?.formBorderStyle ?? 'none';
    const formBorder =
        formBorderStyle === 'none'
            ? undefined
            : `${formStyling?.formBorderWidth ?? 0}px ${formBorderStyle} ${formStyling?.formBorderColor ?? 'rgba(0, 0, 0, 0.23)'}`;
    const formBoxShadow = formStyling?.formShadow
        ? buildBoxShadow(formStyling.formShadow)
        : '0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)';
    const formMargin = formStyling?.margin
        ? `${sideValue(formStyling.margin.top)} ${sideValue(formStyling.margin.right)} ${sideValue(formStyling.margin.bottom)} ${sideValue(formStyling.margin.left)}`
        : undefined;
    const formPadding = formStyling?.padding
        ? `${formStyling.padding.top}px ${formStyling.padding.right}px ${formStyling.padding.bottom}px ${formStyling.padding.left}px`
        : undefined;
    // Without a real backdrop set, the wrapper stays sized to its content
    // (today's default) — only grow it to fill the viewport once there's
    // something behind the card actually worth showing, and never when
    // embedded (see isEmbedded above).
    const hasPageBackground = !isEmbedded && Boolean(formStyling?.backgroundImage || formStyling?.pageBackgroundColor);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: hasPageBackground ? 'center' : undefined,
                    minHeight: hasPageBackground ? '100vh' : undefined,
                    backgroundColor: formStyling?.pageBackgroundColor || undefined,
                    backgroundImage: formStyling?.backgroundImage ? `url(${formStyling.backgroundImage})` : undefined,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                <Card
                    ref={formCardRef}
                    elevation={0}
                    sx={{
                        width: '100%',
                        maxWidth: formStyling?.width ?? 640,
                        margin: formMargin ?? '0 auto',
                        padding: formPadding ?? '32px',
                        backgroundColor: formStyling?.transparentBackground
                            ? 'transparent'
                            : formStyling?.background ?? '#FFFFFF',
                        borderRadius: `${formStyling?.formCornerRadius ?? 8}px`,
                        border: formBorder,
                        boxShadow: formStyling?.transparentBackground ? 'none' : formBoxShadow,
                    }}
                >
                    <ThemeProvider theme={fieldTheme}>
                        {submitError && (
                            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                                {submitError}
                            </Typography>
                        )}
                        {pages.length > 1 && formSettings.showProgress && (
                            <Box sx={{ mb: 2 }}>
                                <Typography
                                    variant="body2"
                                    sx={{ mb: 1, color: formStyling?.progressTextColor || 'text.secondary' }}
                                >
                                    Step {currentPageIndex + 1} of {pages.length}
                                </Typography>
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: `${formStyling?.progressBarHeight ?? 8}px`,
                                        borderRadius: `${formStyling?.progressBarHeight ?? 8}px`,
                                        backgroundColor: formStyling?.progressBarTrackColor || '#E5E7EB',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: `${((currentPageIndex + 1) / pages.length) * 100}%`,
                                            height: '100%',
                                            backgroundColor: getProgressBarColor(formStyling),
                                            transition: 'width 0.2s ease',
                                        }}
                                    />
                                </Box>
                            </Box>
                        )}
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: `${formStyling?.fieldSpacing ?? 24}px`,
                                opacity: submitting ? 0.6 : 1,
                                pointerEvents: submitting ? 'none' : 'auto',
                            }}
                        >
                            {(pages[currentPageIndex]?.fields ?? []).map((field) => (
                                <Box
                                    key={field.id}
                                    sx={{
                                        width: getFieldWidth(field.width, formStyling?.fieldSpacing ?? 24, isMobile, field.type),
                                        boxSizing: 'border-box',
                                    }}
                                >
                                    <InteractiveFieldRenderer
                                        field={field}
                                        value={formData[field.id]}
                                        error={errors[field.id]}
                                        onChange={(value) => handleFieldChange(field.id, value)}
                                        onSubmit={handleSubmit}
                                        formStyling={formStyling}
                                    />
                                </Box>
                            ))}
                        </Box>

                        {/* Bot protection — rendered here rather than as a form.fields entry, so it can never be
                            removed or reordered away from the Form Builder canvas. Only on the last page, next to
                            the actual Submit action — no point verifying before the visitor is ready to submit. */}
                        {isLastPage &&
                            (isTurnstileConfigured ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                    <div ref={turnstileRef}></div>
                                </Box>
                            ) : (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
                                    This form is temporarily unavailable because captcha is not configured.
                                </Typography>
                            ))}

                        {/* Also rendered here rather than as a form.fields entry — Next/Back are a navigation
                            concept, not an answerable field, so an admin can't delete/reorder them away either. */}
                        {pages.length > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                                {currentPageIndex > 0 ? (
                                    <Button
                                        variant="outlined"
                                        onClick={handleBackPage}
                                        sx={getBackButtonSx(formStyling?.backButtonStyle, formStyling)}
                                    >
                                        Back
                                    </Button>
                                ) : (
                                    <Box />
                                )}
                                {!isLastPage && (
                                    <Button
                                        variant="contained"
                                        onClick={handleNextPage}
                                        sx={getSubmitButtonSx(formStyling?.nextButtonStyle, formStyling)}
                                    >
                                        Next
                                    </Button>
                                )}
                            </Box>
                        )}
                    </ThemeProvider>
                </Card>
            </Box>
        </LocalizationProvider>
    );
};

export default FormFillPage;
